import { useState, useEffect, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import type { FileTypeCategoryMapping } from "@/stores/admin/repository";

const ASSIGNMENT_MODES = [
  {
    value: "skip_llm",
    label: "Skip LLM",
    description: "File gets only this category, no LLM call",
  },
  {
    value: "additional",
    label: "Additional",
    description: "File gets this category PLUS LLM results",
  },
];

interface MappingUpdate {
  file_types: string[];
  category_id: number;
  assignment_mode: "skip_llm" | "additional";
  parent_only: boolean;
  priority: number;
  mapping_name: string | null;
  description: string | null;
}

const FileTypeMappingsTable = observer(() => {
  const { documentCategoriesStore } = useStores();
  const [mappingUpdates, setMappingUpdates] = useState<Record<number, MappingUpdate>>({});
  const [savingMapping, setSavingMapping] = useState<number | null>(null);
  const [deletingMapping, setDeletingMapping] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<{ id: number; name: string } | null>(null);

  // New category dialog state
  const [showNewCategoryDialog, setShowNewCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  // Track which context triggered the dialog: "new" for new mapping form, or a mapping ID for existing row
  const [categoryDialogContext, setCategoryDialogContext] = useState<"new" | number>("new");

  // New mapping form state
  const [newMapping, setNewMapping] = useState<MappingUpdate>({
    file_types: [],
    category_id: 0,
    assignment_mode: "skip_llm",
    parent_only: false,
    priority: 100,
    mapping_name: null,
    description: null,
  });
  const [newFileTypesInput, setNewFileTypesInput] = useState("");

  useEffect(() => {
    // A failure lands in documentCategoriesStore.error, which renders below.
    Promise.all([
      documentCategoriesStore.loadCategoryHierarchy(),
      documentCategoriesStore.loadFileTypeMappings(),
    ]).catch(() => undefined);
  }, [documentCategoriesStore]);

  const categoryOptions = documentCategoriesStore.categoryAssignmentOptions;
  const assignableIds = useMemo(
    () => new Set(categoryOptions.map((option) => option.id)),
    [categoryOptions]
  );
  const hierarchyLoaded =
    !documentCategoriesStore.isLoadingCategoryHierarchy &&
    documentCategoriesStore.categoryHierarchy.length > 0;

  /**
   * A mapping may only target a leaf. A parent such as "Design Documentation" is a step
   * in the classifier's refinement chain, so a document filed under it has no real
   * category. Until the hierarchy has loaded nothing can be judged, so nothing is flagged.
   */
  const isUnassignable = (categoryId: number) =>
    hierarchyLoaded && categoryId > 0 && !assignableIds.has(categoryId);

  const categoryLabel = (categoryId: number, fallback: string | null) =>
    documentCategoriesStore.categoryPathLabel(categoryId) ?? fallback ?? `Category #${categoryId}`;

  const unassignableMessage = (categoryId: number, fallback: string | null) =>
    `"${categoryLabel(categoryId, fallback)}" has subcategories, so it is never a final category. Choose one of its subcategories.`;

  useEffect(() => {
    // Initialize local state with current mappings
    const updates: Record<number, MappingUpdate> = {};
    documentCategoriesStore.fileTypeMappingsList.forEach((mapping) => {
      updates[mapping.id] = {
        file_types: mapping.file_types,
        category_id: mapping.category_id,
        assignment_mode: mapping.assignment_mode,
        parent_only: mapping.parent_only,
        priority: mapping.priority,
        mapping_name: mapping.mapping_name,
        description: mapping.description,
      };
    });
    setMappingUpdates(updates);
  }, [documentCategoriesStore.fileTypeMappingsList]);

  const handleSave = async (mappingId: number) => {
    const update = mappingUpdates[mappingId];
    if (!update) return;
    if (isUnassignable(update.category_id)) {
      const original = documentCategoriesStore.getFileTypeMappingById(mappingId);
      toast.error(unassignableMessage(update.category_id, original?.category_name ?? null));
      return;
    }

    setSavingMapping(mappingId);
    try {
      await documentCategoriesStore.updateFileTypeMapping(mappingId, update);
      toast.success("Mapping updated successfully");
    } catch (error) {
      toast.error("Failed to update mapping");
    } finally {
      setSavingMapping(null);
    }
  };

  const handleDeleteClick = useCallback((mappingId: number, mappingName: string | null) => {
    setMappingToDelete({ id: mappingId, name: mappingName || `Mapping #${mappingId}` });
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!mappingToDelete) return;

    setDeletingMapping(mappingToDelete.id);
    try {
      await documentCategoriesStore.deleteFileTypeMapping(mappingToDelete.id);
      toast.success("Mapping deleted successfully");
    } catch (error) {
      toast.error("Failed to delete mapping");
    } finally {
      setDeletingMapping(null);
      setDeleteConfirmOpen(false);
      setMappingToDelete(null);
    }
  }, [mappingToDelete, documentCategoriesStore]);

  const handleFieldChange = (
    mappingId: number,
    field: keyof MappingUpdate,
    value: string | number | boolean | string[] | null
  ) => {
    setMappingUpdates((prev) => ({
      ...prev,
      [mappingId]: {
        ...prev[mappingId],
        [field]: value,
      },
    }));
  };

  const handleFileTypesChange = (mappingId: number, value: string) => {
    // Parse comma-separated file types
    const fileTypes = value
      .split(",")
      .map((t) => t.trim().toLowerCase().replace(/^\./, ""))
      .filter((t) => t.length > 0);
    handleFieldChange(mappingId, "file_types", fileTypes);
  };

  const handleCreate = async () => {
    if (newMapping.file_types.length === 0 || !newMapping.category_id) {
      toast.error("Please specify file types and select a category");
      return;
    }
    if (isUnassignable(newMapping.category_id)) {
      toast.error(unassignableMessage(newMapping.category_id, null));
      return;
    }

    setIsCreating(true);
    try {
      await documentCategoriesStore.createFileTypeMapping(newMapping);
      toast.success("Mapping created successfully");
      // Reset form
      setNewMapping({
        file_types: [],
        category_id: 0,
        assignment_mode: "skip_llm",
        parent_only: false,
        priority: 100,
        mapping_name: null,
        description: null,
      });
      setNewFileTypesInput("");
    } catch (error) {
      toast.error("Failed to create mapping");
    } finally {
      setIsCreating(false);
    }
  };

  const handleNewFileTypesChange = (value: string) => {
    setNewFileTypesInput(value);
    const fileTypes = value
      .split(",")
      .map((t) => t.trim().toLowerCase().replace(/^\./, ""))
      .filter((t) => t.length > 0);
    setNewMapping((prev) => ({ ...prev, file_types: fileTypes }));
  };

  const handleOpenNewCategoryDialog = (context: "new" | number) => {
    setCategoryDialogContext(context);
    setNewCategoryName("");
    setShowNewCategoryDialog(true);
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    setIsCreatingCategory(true);
    try {
      const category = await documentCategoriesStore.createAutoAssignedCategory(
        newCategoryName.trim(),
        null
      );
      // Auto-select the new category in the appropriate context
      if (categoryDialogContext === "new") {
        setNewMapping((prev) => ({ ...prev, category_id: category.id }));
      } else {
        // It's a mapping ID for an existing row
        handleFieldChange(categoryDialogContext, "category_id", category.id);
      }
      setShowNewCategoryDialog(false);
      setNewCategoryName("");
      toast.success(`Category "${category.category_name}" created`);
    } catch {
      toast.error("Failed to create category");
    } finally {
      setIsCreatingCategory(false);
    }
  };

  // Filter mappings based on search query
  const filteredMappings = useMemo(() => {
    if (!searchQuery.trim()) {
      return documentCategoriesStore.fileTypeMappingsList;
    }

    const query = searchQuery.toLowerCase();
    return documentCategoriesStore.fileTypeMappingsList.filter((mapping) => {
      return (
        mapping.file_types.some((t) => t.toLowerCase().includes(query)) ||
        (mapping.mapping_name && mapping.mapping_name.toLowerCase().includes(query)) ||
        (mapping.category_name && mapping.category_name.toLowerCase().includes(query)) ||
        (mapping.description && mapping.description.toLowerCase().includes(query))
      );
    });
  }, [documentCategoriesStore.fileTypeMappingsList, searchQuery]);

  if (documentCategoriesStore.isLoadingMappings) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (documentCategoriesStore.error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-destructive">
        Error loading mappings: {documentCategoriesStore.error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Input
          placeholder="Search mappings..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
      </div>

      {/* Create New Mapping Form */}
      <div className="rounded-md border bg-muted/30 p-4">
        <h4 className="mb-3 font-medium">Create New Mapping</h4>
        <div className="grid grid-cols-6 gap-3">
          <div className="col-span-1">
            <label className="text-xs text-muted-foreground">Name</label>
            <Input
              value={newMapping.mapping_name || ""}
              onChange={(e) =>
                setNewMapping((prev) => ({ ...prev, mapping_name: e.target.value || null }))
              }
              placeholder="e.g., Model Files"
              className="h-8"
            />
          </div>
          <div className="col-span-1">
            <label className="text-xs text-muted-foreground">File Types (comma-separated)</label>
            <Input
              value={newFileTypesInput}
              onChange={(e) => handleNewFileTypesChange(e.target.value)}
              placeholder="ifc, rvt, dwg"
              className="h-8"
            />
          </div>
          <div className="col-span-1">
            <label className="text-xs text-muted-foreground">Category</label>
            <Select
              value={newMapping.category_id ? String(newMapping.category_id) : ""}
              onValueChange={(value) => {
                if (value === "__new__") {
                  handleOpenNewCategoryDialog("new");
                } else {
                  setNewMapping((prev) => ({ ...prev, category_id: Number(value) }));
                }
              }}
            >
              <SelectTrigger className="h-8 w-full">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__new__" className="font-medium text-primary">
                  + Create New Category...
                </SelectItem>
                <SelectSeparator />
                {categoryOptions.map((option) => (
                  <SelectItem key={option.id} value={String(option.id)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <label className="text-xs text-muted-foreground">Mode</label>
            <Select
              value={newMapping.assignment_mode}
              onValueChange={(value) =>
                setNewMapping((prev) => ({
                  ...prev,
                  assignment_mode: value as "skip_llm" | "additional",
                }))
              }
            >
              <SelectTrigger className="h-8 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ASSIGNMENT_MODES.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <label className="text-xs text-muted-foreground">Priority</label>
            <Input
              type="number"
              value={newMapping.priority}
              onChange={(e) =>
                setNewMapping((prev) => ({ ...prev, priority: Number(e.target.value) }))
              }
              className="h-8"
            />
          </div>
          <div className="col-span-1 flex items-end">
            <Button onClick={handleCreate} disabled={isCreating} className="h-8 w-full">
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create
                </>
              )}
            </Button>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Checkbox
            id="new-parent-only"
            checked={newMapping.parent_only}
            onCheckedChange={(checked) =>
              setNewMapping((prev) => ({ ...prev, parent_only: checked === true }))
            }
          />
          <label htmlFor="new-parent-only" className="text-sm text-muted-foreground">
            Parent Only (apply to container files only, not children)
          </label>
        </div>
      </div>

      {/* Existing Mappings Table */}
      {filteredMappings.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground">
          {searchQuery.trim()
            ? "No mappings match your search."
            : "No file type mappings configured. Create one above to bypass LLM classification for specific file types."}
        </div>
      ) : (
        <div className="rounded-md border">
          <Table className="w-full" style={{ tableLayout: "fixed" }}>
            <TableHeader>
              <TableRow>
                <TableHead style={{ width: "15%" }}>Name</TableHead>
                <TableHead style={{ width: "15%" }}>File Types</TableHead>
                <TableHead style={{ width: "15%" }}>Category</TableHead>
                <TableHead style={{ width: "12%" }}>Mode</TableHead>
                <TableHead style={{ width: "8%" }}>Priority</TableHead>
                <TableHead style={{ width: "10%" }}>Parent Only</TableHead>
                <TableHead style={{ width: "15%" }}>Description</TableHead>
                <TableHead style={{ width: "10%" }} className="text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMappings.map((mapping) => {
                const update = mappingUpdates[mapping.id];
                if (!update) return null;

                const original = documentCategoriesStore.getFileTypeMappingById(mapping.id);
                const categoryInvalid = isUnassignable(update.category_id);
                const hasChanges =
                  original &&
                  (JSON.stringify(update.file_types) !== JSON.stringify(original.file_types) ||
                    update.category_id !== original.category_id ||
                    update.assignment_mode !== original.assignment_mode ||
                    update.parent_only !== original.parent_only ||
                    update.priority !== original.priority ||
                    update.mapping_name !== original.mapping_name ||
                    update.description !== original.description);

                return (
                  <TableRow key={mapping.id}>
                    <TableCell style={{ width: "15%" }}>
                      <Input
                        value={update.mapping_name || ""}
                        onChange={(e) =>
                          handleFieldChange(mapping.id, "mapping_name", e.target.value || null)
                        }
                        placeholder="Mapping name"
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell style={{ width: "15%" }}>
                      <Input
                        value={update.file_types.join(", ")}
                        onChange={(e) => handleFileTypesChange(mapping.id, e.target.value)}
                        placeholder="ifc, rvt, dwg"
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell style={{ width: "15%" }}>
                      <Select
                        value={String(update.category_id)}
                        onValueChange={(value) => {
                          if (value === "__new__") {
                            handleOpenNewCategoryDialog(mapping.id);
                          } else {
                            handleFieldChange(mapping.id, "category_id", Number(value));
                          }
                        }}
                      >
                        <SelectTrigger
                          className={
                            categoryInvalid ? "h-8 w-full border-destructive" : "h-8 w-full"
                          }
                          aria-invalid={categoryInvalid}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__new__" className="font-medium text-primary">
                            + Create New Category...
                          </SelectItem>
                          <SelectSeparator />
                          {categoryInvalid && (
                            // Keeps the saved value visible: a Radix Select shows nothing
                            // for a value no mounted item carries.
                            <SelectItem value={String(update.category_id)} disabled>
                              {categoryLabel(update.category_id, mapping.category_name)}
                            </SelectItem>
                          )}
                          {categoryOptions.map((option) => (
                            <SelectItem key={option.id} value={String(option.id)}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {categoryInvalid && (
                        <p className="mt-1 text-xs text-destructive">
                          Has subcategories — choose one of them
                        </p>
                      )}
                    </TableCell>
                    <TableCell style={{ width: "12%" }}>
                      <Select
                        value={update.assignment_mode}
                        onValueChange={(value) =>
                          handleFieldChange(
                            mapping.id,
                            "assignment_mode",
                            value as "skip_llm" | "additional"
                          )
                        }
                      >
                        <SelectTrigger className="h-8 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ASSIGNMENT_MODES.map((mode) => (
                            <SelectItem key={mode.value} value={mode.value}>
                              {mode.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell style={{ width: "8%" }}>
                      <Input
                        type="number"
                        value={update.priority}
                        onChange={(e) =>
                          handleFieldChange(mapping.id, "priority", Number(e.target.value))
                        }
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell style={{ width: "10%" }}>
                      <Checkbox
                        checked={update.parent_only}
                        onCheckedChange={(checked) =>
                          handleFieldChange(mapping.id, "parent_only", checked === true)
                        }
                      />
                    </TableCell>
                    <TableCell style={{ width: "15%" }}>
                      <Input
                        value={update.description || ""}
                        onChange={(e) =>
                          handleFieldChange(mapping.id, "description", e.target.value || null)
                        }
                        placeholder="Description"
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell style={{ width: "10%" }} className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSave(mapping.id)}
                          disabled={!hasChanges || savingMapping === mapping.id}
                        >
                          {savingMapping === mapping.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Save"
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteClick(mapping.id, mapping.mapping_name)}
                          disabled={deletingMapping === mapping.id}
                        >
                          {deletingMapping === mapping.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        title="Delete File Type Mapping"
        description={`Are you sure you want to delete "${mappingToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
      />

      <Dialog open={showNewCategoryDialog} onOpenChange={setShowNewCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Category</DialogTitle>
            <DialogDescription>
              This category will be used for file-type mappings only and will NOT appear in LLM
              classification prompts.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              placeholder="Category name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newCategoryName.trim()) {
                  handleCreateCategory();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCategoryDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateCategory}
              disabled={isCreatingCategory || !newCategoryName.trim()}
            >
              {isCreatingCategory ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default FileTypeMappingsTable;
